// src/components/Admin/MembersTable.jsx
import React, { useState } from 'react';
import { Check, X, CreditCard, ExternalLink, Loader2 } from 'lucide-react';
import PrivilegeCardModal from './PrivilegeCardModal';
import './MembersTable.css';

export default function MembersTable({ members, onUpdateStatus }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCardMember, setSelectedCardMember] = useState(null);
  const [loadingCardId, setLoadingCardId] = useState(null);

  // Fetch full card profile only for approved members with a code
  const handleOpenCardModal = async (member) => {
    if (member.status !== 'approved' || !member.privilege_code) {
      alert('Card view is restricted until the member is approved.');
      return;
    }

    const identifier = member.privilege_code || member.id;
    setLoadingCardId(member.id);

    try {
      const res = await fetch(`https://api.lbnhyderabad.com/api/admin/members/card/${identifier}`);
      const json = await res.json();

      if (res.ok && json.success) {
        setSelectedCardMember(json.member);
      } else {
        setSelectedCardMember(member);
      }
    } catch (err) {
      console.error('Failed to fetch card details, using row data:', err);
      setSelectedCardMember(member);
    } finally {
      setLoadingCardId(null);
    }
  };

  const filtered = members.filter(m =>
    m.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.membership_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.district?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.privilege_code?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="members-module-card">
      {/* 3D Card Modal - Rendered only when an approved member is selected */}
      {selectedCardMember && (
        <PrivilegeCardModal 
          member={selectedCardMember} 
          onClose={() => setSelectedCardMember(null)} 
        />
      )}

      <div className="members-toolbar">
        <div className="members-toolbar-title">
          <h3>Member Accreditation Directory</h3>
          <span className="member-count-pill">{members.length} Enrolled</span>
        </div>

        <input
          type="text"
          className="members-filter-input"
          placeholder="Search name, ID, 8-char code, district..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="members-table-scroll">
        <table className="members-table">
          <thead>
            <tr>
              <th>Member Details</th>
              <th>Membership ID</th>
              <th>Privilege Code</th>
              <th>District & Club</th>
              <th>Contact Details</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(member => {
              const isApproved = member.status === 'approved' && Boolean(member.privilege_code);

              return (
                <tr key={member.id}>
                  {/* Member Name & Photo Avatar */}
                  <td>
                    <div className="member-identity-cell">
                      <div className="member-avatar-chip">
                        {member.full_name ? member.full_name.charAt(0).toUpperCase() : 'M'}
                      </div>
                      <div>
                        <strong style={{ color: '#003666', fontSize: '0.86rem' }}>
                          {member.full_name}
                        </strong>
                      </div>
                    </div>
                  </td>

                  {/* Membership Number */}
                  <td>
                    <span className="membership-id-badge">
                      {member.membership_number}
                    </span>
                  </td>

                  {/* 8-Character Unique Privilege Code (Shown ONLY after Approval) */}
                  <td>
                    {isApproved ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ 
                          fontFamily: 'monospace', 
                          fontWeight: 800, 
                          color: '#00529b',
                          background: '#f1f5f9',
                          padding: '2px 7px',
                          border: '1px solid #cbd5e1',
                          borderRadius: '6px',
                          letterSpacing: '1px'
                        }}>
                          {member.privilege_code}
                        </span>
                        <a 
                          href={`/card/view/${member.privilege_code}`} 
                          target="_blank" 
                          rel="noreferrer"
                          title="Open Digital ID"
                          style={{ color: '#00529b', display: 'inline-flex', alignItems: 'center' }}
                        >
                          <ExternalLink size={12} />
                        </a>
                      </div>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: '0.74rem', fontStyle: 'italic' }}>
                        {member.status === 'rejected' ? 'Application Rejected' : 'Awaiting Approval'}
                      </span>
                    )}
                  </td>

                  {/* District & Club */}
                  <td>
                    <span className="district-tag">District {member.district}</span>
                    <span style={{ fontSize: '0.78rem' }}>{member.club_name}</span>
                  </td>

                  {/* Contact */}
                  <td>
                    <div style={{ fontWeight: 600 }}>{member.mobile}</div>
                    <small style={{ color: '#64748b' }}>{member.email}</small>
                  </td>

                  {/* Status Badge */}
                  <td>
                    <span className={`status-badge-member ${member.status}`}>
                      {member.status}
                    </span>
                  </td>

                  {/* Actions */}
                  <td>
                    <div className="member-action-suite">
                      {/* CARD VIEW: Rendered ONLY if the member is approved */}
                      {isApproved && (
                        <button
                          className="btn-approve-lux"
                          style={{ background: '#00529b', color: '#febf2c', padding: '6px 12px' }}
                          onClick={() => handleOpenCardModal(member)}
                          disabled={loadingCardId === member.id}
                          title="Generate & View 3D Printable Card"
                        >
                          {loadingCardId === member.id ? (
                            <Loader2 size={13} className="spin-loader" />
                          ) : (
                            <CreditCard size={13} />
                          )}
                          <span>Card View</span>
                        </button>
                      )}

                      {/* APPROVE: Displayed when not yet approved */}
                      {member.status !== 'approved' && (
                        <button
                          className="btn-approve-lux"
                          onClick={() => onUpdateStatus(member.id, 'approved')}
                          title="Approve Member & Issue 8-Character Privilege Code"
                        >
                          <Check size={13} />
                          <span>Approve</span>
                        </button>
                      )}

                      {/* REJECT: Displayed when not rejected */}
                      {member.status !== 'rejected' && (
                        <button
                          className="btn-reject-lux"
                          onClick={() => onUpdateStatus(member.id, 'rejected')}
                          title="Reject Application"
                        >
                          <X size={13} />
                          <span>Reject</span>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}

            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} style={{ textAlign: 'center', padding: '32px', color: '#94a3b8' }}>
                  No members found matching your search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}